'use client';

// Warms the route a Next.js <Link> points to by flipping the Link's `prefetch`
// prop once the user shows intent (hover / keyboard focus) or the link scrolls
// near the viewport. Prefetching stays off by default, so the app only pays
// for routes the user is actually likely to visit. Unlike a manual
// router.prefetch() call, letting <Link prefetch> do the work prefetches the
// full page payload (not just the static shell).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type NetworkInformation = {
  saveData?: boolean;
  effectiveType?: string;
};

const getConnection = (): NetworkInformation | null => {
  if (typeof navigator === 'undefined') return null;
  return (navigator as Navigator & { connection?: NetworkInformation }).connection ?? null;
};

const hasRIC = typeof window !== 'undefined' && 'requestIdleCallback' in window;

const requestIdle = (cb: () => void): number =>
  hasRIC
    ? window.requestIdleCallback(cb, { timeout: 2000 })
    : requestAnimationFrame(() => window.setTimeout(cb, 500));

const cancelIdle = (id: number): void => {
  if (hasRIC) {
    window.cancelIdleCallback(id);
  } else {
    window.clearTimeout(id);
  }
};

/** ------------ types ------------ */
export type UseLinkPrefetchOptions = {
  /** Prefetch on pointer hover / keyboard focus. Default true. */
  prefetchOnHover?: boolean;

  /** Prefetch when the link scrolls near the viewport. Default true. */
  prefetchWhenVisible?: boolean;

  /** IntersectionObserver root margin for early prefetch. Default '400px'. */
  rootMargin?: string;

  /** Delay before a hover triggers prefetch, to filter drive-bys. Default 80. */
  hoverDelayMs?: number;
};

type BindProps = {
  ref: (el: HTMLElement | null) => void;
  onPointerEnter?: (e: any) => void;
  onPointerLeave?: (e: any) => void;
  onFocus?: (e: any) => void;
};

export type UseLinkPrefetchResult = Readonly<BindProps> & {
  /** Becomes true once intent/visibility is detected — feed into <Link prefetch>. */
  shouldPrefetch: boolean;
};

/** ------------ hook ------------ */
export const useLinkPrefetch = ({
  prefetchOnHover = true,
  prefetchWhenVisible = true,
  rootMargin = '400px',
  hoverDelayMs = 80,
}: UseLinkPrefetchOptions = {}): UseLinkPrefetchResult => {
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  const ioRef = useRef<IntersectionObserver | null>(null);
  const idleIdRef = useRef<number | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  const { saveData, isVerySlow } = useMemo(() => {
    const conn = getConnection();
    return {
      saveData: !!conn?.saveData,
      isVerySlow: conn?.effectiveType === '2g' || conn?.effectiveType === 'slow-2g',
    };
  }, []);

  // Touch devices have no real hover — skip hover prefetch but keep focus (a11y).
  const isTouchOnly = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      ((navigator as Navigator & { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0
    );
  }, []);

  const enable = useCallback(() => {
    if (saveData || isVerySlow) return;
    setShouldPrefetch(true);
  }, [saveData, isVerySlow]);

  // Visible links wait for idle time before prefetching (low priority).
  const enableWhenIdle = useCallback(() => {
    if (saveData || isVerySlow) return;
    // Skip if the page is in a background tab.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (idleIdRef.current != null) return;
    idleIdRef.current = requestIdle(() => {
      idleIdRef.current = null;
      enable();
    });
  }, [saveData, isVerySlow, enable]);

  const setupVisibilityObserver = useCallback(
    (el: HTMLElement) => {
      if (!prefetchWhenVisible || saveData || isVerySlow) return;
      if (typeof IntersectionObserver === 'undefined') {
        // No IntersectionObserver support — prefetch during idle time instead.
        enableWhenIdle();
        return;
      }

      ioRef.current?.disconnect();
      ioRef.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            ioRef.current?.disconnect();
            ioRef.current = null;
            enableWhenIdle();
          }
        },
        { rootMargin },
      );
      ioRef.current.observe(el);
    },
    [prefetchWhenVisible, saveData, isVerySlow, rootMargin, enableWhenIdle],
  );

  // Bindings
  const bind: BindProps = {
    ref: (el) => {
      if (el) {
        if (!shouldPrefetch) setupVisibilityObserver(el);
      } else {
        ioRef.current?.disconnect();
        ioRef.current = null;
      }
    },
  };

  // Pointer hover with a small delay (filters drive-bys)
  if (prefetchOnHover && !isTouchOnly) {
    bind.onPointerEnter = () => {
      if (hoverTimerRef.current != null) return;
      hoverTimerRef.current = window.setTimeout(() => {
        hoverTimerRef.current = null;
        enable();
      }, hoverDelayMs);
    };
    bind.onPointerLeave = () => {
      if (hoverTimerRef.current != null) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }

  // Keyboard focus (always allowed — good for a11y)
  if (prefetchOnHover) {
    bind.onFocus = () => enable();
  }

  // Cleanup timers and observer on unmount
  useEffect(
    () => () => {
      if (idleIdRef.current != null) cancelIdle(idleIdRef.current);
      if (hoverTimerRef.current != null) window.clearTimeout(hoverTimerRef.current);
      ioRef.current?.disconnect();
    },
    [],
  );

  return { ...bind, shouldPrefetch };
};
